import { useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { VibeRead } from '../../types/api';
import { fetchVibes } from './generationApi';

export interface VibesResult {
  vibes: VibeRead[];
  isLoading: boolean;
  error: string | null;
}

/** Result of the last completed load. Vibes are a global lookup, not per business. */
interface LoadedVibes {
  vibes: VibeRead[];
  error: string | null;
}

/** Stable empty result, so consumers do not see a new array identity per render. */
const NO_VIBES: never[] = [];

/**
 * The seeded vibe list (funny, trendy, professional).
 *
 * Same shape as the knowledge-base loaders: one effect, no synchronous state
 * write, visible state derived during render. A failure is not fatal — vibe is
 * optional on the brief, so the form falls back to "no preference".
 */
export function useVibes(): VibesResult {
  const [loaded, setLoaded] = useState<LoadedVibes | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const vibes = await fetchVibes(controller.signal);
        if (cancelled) return;
        setLoaded({ vibes, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          vibes: [],
          error: getErrorMessage(caught, 'Could not load the vibe list.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return {
    vibes: loaded?.vibes ?? NO_VIBES,
    isLoading: loaded === null,
    error: loaded?.error ?? null,
  };
}
